import { isDefined } from "@clipboard-health/util-ts";
import { fetchPlacePredictions } from "@src/appV2/lib/GoogleMaps/googleMaps";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import { useQuery } from "@tanstack/react-query";

interface UseAddressAutocompleteOptions {
  query: string;
  debounceMs?: number;
}

interface UseAddressAutocompleteResult {
  predictions: google.maps.places.AutocompletePrediction[];
  isLoading: boolean;
  hasError: boolean;
  hasNoResults: boolean;
}

export function useAddressAutocomplete({
  query,
  debounceMs = 200,
}: UseAddressAutocompleteOptions): UseAddressAutocompleteResult {
  const debouncedQuery = useDebounce({
    value: query.trim(),
    debounceTimeInMs: debounceMs,
  });

  // Query for autocomplete predictions
  const {
    data: predictionsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["addressAutocomplete", debouncedQuery],
    queryFn: async () => await fetchPlacePredictions(debouncedQuery),
    enabled: isDefined(debouncedQuery) && debouncedQuery.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });

  const predictions = predictionsData?.predictions ?? [];
  const hasNoResults =
    !isLoading &&
    !isError &&
    debouncedQuery.length > 0 &&
    predictionsData?.status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS;

  return {
    predictions,
    isLoading,
    hasError: isError,
    hasNoResults,
  };
}
